import type { LoggerInterface } from '../../logger'
import type {
  AjaxIdempotency,
  TypeCallOptions,
  TypeCallParams,
  TypeHttp,
  ICallBatchOptions,
  BatchCommandsArrayUniversal,
  BatchCommandsObjectUniversal,
  BatchNamedCommandsUniversal,
  ICallBatchResult
} from '../../types/http'
import type { RestrictionManagerStats, RestrictionParams } from '../../types/limiters'
import type { AuthActions, AuthData } from '../../types/auth'
import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import type { Result } from '../result'
import type { SuccessPayload } from '../../types/payloads'
import axios, { AxiosError } from 'axios'
import { LoggerFactory } from '../../logger'
import { RequestIdGenerator } from '../request-id-generator'
import { ParamsFactory } from './limiters/params-factory'
import { RestrictionManager } from './limiters/manager'
import { AjaxError } from './ajax-error'
import { parseErrorPayload } from './parse-error-payload'
import { AjaxResult } from './ajax-result'
import { redactSensitiveParams } from './redact'
import { HTTP_OPTION_KEYS, pickHttpOptions } from './http-options'
import { Type } from '../../tools/type'
import { isBrowserLikeRuntime } from '../../tools/environment'
import { ApiVersion } from '../../types/b24'
import { SdkError } from '../sdk-error'

// Logger payloads are truncated so a large params / result / error body can't
// flood a wired logger sink. Shared by the post/send, post/response, and
// post/catchError log callsites (#236).
// `value` is deliberately `unknown` rather than `string`: every callsite feeds it
// `JSON.stringify(...)`, whose return type is a lying `string` — it actually
// returns the VALUE `undefined` for `undefined`, a function, or a symbol. A
// success body with no `result` key (`rest.documentation.openapi`) and an
// AxiosError with no `response` (network failure, timeout, CORS) both hit that
// case, and reading `.length` off it threw a `TypeError` from inside the logging
// call — which the request path then re-wrapped as `JSSDK_UNKNOWN_ERROR` /
// status 0, destroying the real error. Coercing here keeps the guarantee at the
// one place all three callsites share, so a new callsite can't reintroduce it. (#338)
const LOG_MAX_LENGTH = 300
const LOG_SLICE_LENGTH = 100
export function truncateForLog(value: unknown): string {
  const text = typeof value === 'string' ? value : String(value)
  return text.length > LOG_MAX_LENGTH
    ? text.slice(0, LOG_SLICE_LENGTH) + '...'
    : text
}

/**
 * Header for a per-request idempotency key on `restApi:v3`. Sent in this
 * casing; read back case-insensitively, because the portal answers lowercased
 * and axios normalises differently per adapter.
 */
export const IDEMPOTENCY_KEY_HEADER = 'Idempotency-Key'
const IDEMPOTENCY_KEY_HEADER_LOWER = 'idempotency-key'
const IDEMPOTENT_REPLAYED_HEADER_LOWER = 'idempotent-replayed'

/**
 * A valid idempotency key: 1-255 printable ASCII characters, no whitespace and
 * no control characters, as the portal's reference states.
 *
 * Checked before the key reaches axios so a malformed one fails as a readable
 * SDK error rather than as an opaque `ERR_INVALID_CHAR` out of Node's HTTP
 * client (or a silent rejection in a browser). The bound is the documented
 * one; a portal that later widens it would need this widened with it.
 */
const IDEMPOTENCY_KEY_RE = /^[\u0021-\u007E]{1,255}$/

/**
 * Picks the two idempotency headers out of a response header bag, by
 * lowercased name.
 *
 * Deliberately two named headers rather than the whole bag: putting arbitrary
 * response headers on a public result object would also put them into every
 * wired logger sink, and the SDK does not know what a portal or a proxy in
 * front of it may add there.
 *
 * Returns `undefined` when neither header is present, so an ordinary response
 * carries no extra field at all.
 */
export function readIdempotencyHeaders(headers: unknown): AjaxIdempotency | undefined {
  if (null === headers || typeof headers !== 'object') {
    return undefined
  }

  let key: string | undefined
  let replayed: boolean | undefined

  for (const [name, rawValue] of Object.entries(headers as Record<string, unknown>)) {
    const lowerName = name.toLowerCase()

    // A repeated header name reaches us as an array through Node's http
    // adapter. Bitrix24 does not send either of these twice, but a proxy in
    // front of it may — and reading the array itself would stringify to
    // `true,true`, which matches nothing and would swallow a real replay.
    const value = Array.isArray(rawValue) ? rawValue[0] : rawValue

    if (IDEMPOTENCY_KEY_HEADER_LOWER === lowerName && typeof value === 'string') {
      key = value
    } else if (IDEMPOTENT_REPLAYED_HEADER_LOWER === lowerName) {
      // The portal sends the string `true`; accept the boolean too, since an
      // adapter or a test double may have parsed it already.
      replayed = true === value || 'true' === String(value).trim().toLowerCase()
    }
  }

  if (undefined === key && undefined === replayed) {
    return undefined
  }

  return { key, replayed: replayed ?? false }
}

export type AjaxResponse<T = unknown> = {
  status: number
  payload: SuccessPayload<T>
  /** Present only when the response carried an idempotency header. */
  idempotency?: AjaxIdempotency
}

export type TypePrepareParams = TypeCallParams & {
  data?: Record<string, any>
  auth?: string
}

/**
 * Does this runtime apply CORS to an outbound request?
 *
 * One question, one predicate: {@link isBrowserLikeRuntime}, which is true for a
 * browser and for all three worker kinds. This wrapper exists so the call sites
 * below read as the question they are actually asking — a credential decision —
 * rather than as a runtime check whose relevance the reader has to reconstruct.
 *
 * It used to probe `WorkerGlobalScope` here, beside a `getEnvironment()` that
 * called a worker a server. Two near-identical predicates invited exactly the
 * "simplification" that would have reopened the worker case, in a code path
 * whose failure mode is a request that never leaves the browser (#505). The
 * worker now has a name of its own, and this is a rename of that name.
 */
function isCorsEnforcedRuntime(): boolean {
  return isBrowserLikeRuntime()
}

/**
 * Which axios adapter to ask for, when there is a reason to ask at all.
 *
 * Axios walks its default `['xhr', 'http', 'fetch']` and takes the first
 * supported* entry, so anywhere `XMLHttpRequest` exists — a window, a dedicated
 * worker, a shared worker — it picks **XHR** and never reaches `fetch`. That is a
 * 1999 API chosen by ordering rather than by merit: no streaming, no
 * `AbortSignal` beyond `abort()`, and headers that arrive as one folded string.
 *
 * So in a browser-like runtime this asks for `fetch` instead. Everywhere else —
 * Node, most obviously — nothing is returned and axios decides for itself: there
 * XHR does not exist, the `http` adapter is already what gets picked, and it is
 * the better one for that runtime.
 *
 * Guarded on `fetch` actually being there rather than on a version check, since
 * the point is to reach it, not to assert an era.
 *
 * **One behaviour moves with it.** The fetch adapter reads `maxRedirects`, as
 * `redirect: 'manual'`; the XHR adapter ignores it outright. The only branch that
 * sets it in a browser is the query-auth one below, which exists to stop an
 * access token following a redirect — so the change is that the guard now bites
 * where it used to be inert: a redirecting deployment gets an opaque response
 * (status 0, empty body) instead of a silent hop carrying the credential.
 *
 * Opaque is not the same as rejected. `settle` resolves any response whose
 * status is falsy *before* `validateStatus` is consulted, and unlike the XHR
 * adapter — which rejects `status === 0` as `ECONNABORTED` on its own — the
 * fetch adapter has no such guard, so that blocked redirect would otherwise
 * reach the caller as an empty **success**. `_makeAxiosRequest` states it
 * instead, on the branch that asked for `maxRedirects: 0` and nowhere else.
 *
 * Returned as a spread-able object rather than a value so "no opinion" and
 * "`undefined` on purpose" stay distinguishable, and placed **before** the
 * caller's `options` so an explicit `adapter` always wins.
 */
/**
 * Options objects whose dropped keys have already been reported, so the second
 * transport built from the same object stays quiet. Weak, so nothing here keeps
 * a caller's config alive.
 */
const reportedHttpOptions = new WeakSet<object>()

function preferredAdapter(): { adapter?: 'fetch' } {
  // `isBrowserLikeRuntime`, not `isCorsEnforcedRuntime`: the two are the same
  // predicate today, and this is not a credential decision — it asks which
  // adapter belongs in this runtime. Naming the question it asks is what keeps
  // the two free to diverge.
  if (!isBrowserLikeRuntime()) {
    return {}
  }

  return 'function' === typeof globalThis.fetch ? { adapter: 'fetch' } : {}
}

/**
 * Abstract base class for all Bitrix24 REST API HTTP transports.
 *
 * Provides shared infrastructure used by {@link HttpV2} and {@link HttpV3}: Axios instance
 * lifecycle, auth token management (including coalesced refresh on 401), rate/operating/adaptive
 * limiting via {@link RestrictionManager}, request-id generation, structured logging with
 * payload truncation, and request metrics. Concrete subclasses implement version-specific
 * batch strategies.
 *
 * @link https://bitrix24.github.io/b24jssdk/
 */
export abstract class AbstractHttp implements TypeHttp {
  protected _clientAxios: AxiosInstance
  protected _authActions: AuthActions
  protected _requestIdGenerator: RequestIdGenerator
  protected _restrictionManager: RestrictionManager

  /**
   * In-flight token refresh, shared so concurrent 401s coalesce into a single
   * `refreshAuth()` round-trip — avoids OAuth refresh-token reuse errors when a
   * burst of requests expires together. (#182)
   */
  protected _pendingRefresh: Promise<AuthData> | null = null

  protected _logger: LoggerInterface

  protected _isClientSideWarning: boolean = false
  protected _clientSideWarningMessage: string = ''

  protected _version: ApiVersion

  protected _metrics = {
    totalRequests: 0,
    successfulRequests: 0,
    failedRequests: 0,
    totalDuration: 0,
    byMethod: new Map<string, { count: number, totalDuration: number }>(),
    lastErrors: [] as Array<{ method: string, error: string, timestamp: number }>
  }

  constructor(
    authActions: AuthActions,
    options?: null | object,
    restrictionParams?: Partial<RestrictionParams>
  ) {
    this._version = ApiVersion.v2

    this._logger = LoggerFactory.createNullLogger()

    const defaultHeaders: Record<string, string> = {}

    if (this.isServerSide()) {
      defaultHeaders['User-Agent'] = '__SDK_USER_AGENT__/__SDK_VERSION__'
    }

    this._authActions = authActions
    this._requestIdGenerator = new RequestIdGenerator()

    // Filtered rather than spread as given: `TypeHttpOptions` names the keys a
    // caller may set, but a type cannot enforce that. Excess-property checking
    // fires only on a direct object literal with no overlapping key, so one
    // allowed key beside `transformRequest` — or a variable, or a call from
    // plain JavaScript — used to carry anything straight into axios. See
    // `pickHttpOptions`, which is also where the key list lives.
    const { picked: httpOptions, dropped: droppedHttpOptions } = pickHttpOptions(options)

    this._clientAxios = axios.create({
      timeout: 30_000,
      timeoutErrorMessage: 'Request timeout exceeded',
      ...preferredAdapter(),
      ...httpOptions,
      // headers last so the merged default + caller headers aren't wiped by an
      // `options.headers` (or the previous `headers: undefined`) spread (#144).
      // Read from `options` rather than from the filtered config: the merge
      // predates `TypeHttpOptions` and is left as it was. The SDK's own
      // `Content-Type` is decided per request and beats anything set here
      // (measured, lowercase spelling included); `Authorization` is per-request
      // only on the OAuth header branch, so on a webhook an instance header of
      // that name does reach the wire — which is one more reason the type does
      // not offer this key.
      headers: {
        ...defaultHeaders,
        ...((options as any)?.headers ?? {})
      }
    })

    // Once per options object, not once per transport: a `B24Hook` builds
    // `HttpV2` and `HttpV3` from the same object, and two identical warnings
    // naming neither transport read as a bug in the warning.
    if (droppedHttpOptions.length > 0 && !reportedHttpOptions.has(options as object)) {
      reportedHttpOptions.add(options as object)

      // Names only, never values: a `headers` entry a caller tried to set here
      // can carry an `Authorization` token, and this reaches whatever sink the
      // app wired up. Forced, because the default logger is silent and a
      // silently ignored option is the failure this guard exists to prevent.
      LoggerFactory.forcedLog(
        this._logger,
        'warning',
        'httpOptions: keys not accepted at construction were dropped',
        {
          dropped: droppedHttpOptions.join(', '),
          accepted: HTTP_OPTION_KEYS.join(', '),
          hint: 'set them on getHttpClient(version).ajaxClient.defaults instead'
        }
      ).catch(() => {})
    }

    /**
     * Basic parameters of restrictions
     */
    const params: RestrictionParams = {
      ...ParamsFactory.getDefault(),
      ...restrictionParams
    }

    this._restrictionManager = new RestrictionManager(params)
  }

  get apiVersion(): ApiVersion {
    return this._version
  }

  get ajaxClient(): AxiosInstance {
    return this._clientAxios
  }

  // region Logger ////
  public setLogger(logger: LoggerInterface): void {
    this._logger = logger
    this._restrictionManager.setLogger(this._logger)
  }

  public getLogger(): LoggerInterface {
    return this._logger
  }
  // endregion ////

  // region RestrictionManager ////
  public async setRestrictionManagerParams(params: RestrictionParams): Promise<void> {
    await this._restrictionManager.setConfig(params)
  }

  public getRestrictionManagerParams(): RestrictionParams {
    return this._restrictionManager.getParams()
  }

  /**
   * @inheritDoc
   */
  public getStats(): RestrictionManagerStats & {
    adaptiveDelayAvg: number
    errorCounts: Record<string, number>
    totalRequests: number
    successfulRequests: number
    failedRequests: number
    totalDuration: number
    byMethod: Map<string, { count: number, totalDuration: number }>
    lastErrors: { method: string, error: string, timestamp: number }[]
  } {
    return {
      ...this._restrictionManager.getStats(),
      totalRequests: this._metrics.totalRequests,
      successfulRequests: this._metrics.successfulRequests,
      failedRequests: this._metrics.failedRequests,
      totalDuration: this._metrics.totalDuration,
      byMethod: this._metrics.byMethod,
      lastErrors: this._metrics.lastErrors
    }
  }

  /**
   * @inheritDoc
   */
  public async reset(): Promise<void> {
    this._metrics.totalRequests = 0
    this._metrics.successfulRequests = 0
    this._metrics.failedRequests = 0
    this._metrics.totalDuration = 0
    this._metrics.byMethod.clear()
    this._metrics.lastErrors = []

    return this._restrictionManager.reset()
  }
  // endregion ////

  // region Metrics ////
  protected _updateMetrics(
    method: string,
    isSuccess: boolean,
    duration: number,
    error?: unknown
  ): void {
    this._metrics.totalRequests++

    if (isSuccess) {
      this._metrics.successfulRequests++
    } else {
      this._metrics.failedRequests++

      if (error instanceof AjaxError) {
        this._metrics.lastErrors.push({
          method,
          error: error.message,
          timestamp: Date.now()
        })

        if (this._metrics.lastErrors.length > 100) {
          this._metrics.lastErrors = this._metrics.lastErrors.slice(-100)
        }
      }
    }

    // Metrics by Method
    if (!this._metrics.byMethod.has(method)) {
      this._metrics.byMethod.set(method, { count: 0, totalDuration: 0 })
    }

    const methodMetrics = this._metrics.byMethod.get(method)!
    methodMetrics.count++
    methodMetrics.totalDuration += duration
  }
  // endregion ////

  // region Actions Call ////
  // region batch ////
  public abstract batch<T = unknown>(
    calls: BatchCommandsArrayUniversal | BatchCommandsObjectUniversal | BatchNamedCommandsUniversal,
    options?: ICallBatchOptions
  ): Promise<Result<ICallBatchResult<T>>>
  // endregion ////

  protected _validateParams(requestId: string, method: string, params: TypeCallParams): void {
    // Checking for cyclic references (especially important when logging)
    try {
      JSON.stringify(params)
    } catch (error) {
      throw new AjaxError({
        code: 'JSSDK_INVALID_PARAMS',
        description: 'Parameters contain circular references',
        status: 400,
        requestInfo: { method, params, requestId },
        originalError: error
      })
    }

    // Size check (It is especially important for batch)
    // const paramsSize = JSON.stringify(params).length
    // if (paramsSize > 1024 * 1024) { // 1MB
    //   throw new AjaxError({
    //     code: 'JSSDK_PARAMS_TOO_LARGE',
    //     description: `Parameters too large: ${(paramsSize / 1024 / 1024).toFixed(2)}MB`,
    //     status: 400,
    //     requestInfo: { method, params, requestId },
    //     originalError: null
    //   })
    // }
  }

  /**
   * Calling the RestApi function
   * @param method - REST API method name
   * @param params - Parameters for the method.
   * @param requestId - Request id
   * @param options - Per-request transport options (currently `idempotencyKey`)
   * @returns Promise with AjaxResult
   */
  public async call<T = unknown>(method: string, params: TypeCallParams, requestId?: string, options?: TypeCallOptions): Promise<AjaxResult<T>> {
    requestId = requestId ?? this._requestIdGenerator.getRequestId()
    const maxRetries = this._restrictionManager.getParams().maxRetries!

    this._validateParams(requestId, method, params)
    this._logRequest(requestId, method, params)

    // Built once, before the retry loop, and threaded down as config rather
    // than as options. Three reasons, all of them found the hard way:
    // a malformed key must fail as `JSSDK_HTTP_INVALID_IDEMPOTENCY_KEY` — built
    // inside the loop it was thrown inside the try, converted by
    // `_convertToAjaxError`, and reached the caller as `JSSDK_UNKNOWN_ERROR`;
    // `HttpV2`'s dropped-key warning belongs to the call, not to each attempt;
    // and the same config must go out on every attempt, since a retry of one
    // operation has to carry the same key. (#462)
    const requestConfig = this._prepareRequestConfig(requestId, method, options)

    let lastError: AjaxError | null = null
    const startTime = Date.now()

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        this._logAttempt(requestId, method, attempt + 1, maxRetries)

        // Apply operating limits via the manager
        await this._restrictionManager.applyOperatingLimits(requestId, method, params)

        // 3. We execute the request taking into account authorization, rate limit, and update operating statistics.
        const result = await this._executeSingleCall<T>(requestId, method, params, requestConfig)
        const duration = Date.now() - startTime

        // 6. Updating statistics
        this._restrictionManager.resetErrors(method)
        this._updateMetrics(method, true, duration)

        // Log the results
        this._logSuccessfulRequest(requestId, method, duration)
        return result
      } catch (error: unknown) {
        lastError = this._convertToAjaxError(requestId, error, method, params)

        const duration = Date.now() - startTime

        this._restrictionManager.incrementError(method)
        this._updateMetrics(method, false, duration, lastError)

        // Log the results
        this._logFailedRequest(requestId, method, attempt + 1, maxRetries, lastError)

        if (attempt + 1 < maxRetries) {
          const waitTime = await this._restrictionManager.handleError(requestId, method, params, lastError, attempt)
          // We don't repeat if waitTime === 0
          if (waitTime > 0) {
            this._restrictionManager.incrementStats('limitHits')

            this._logAttemptRetryWaiteDelay(requestId, method, waitTime, attempt + 1, maxRetries)
            await this._restrictionManager.waiteDelay(waitTime)

            this._restrictionManager.incrementStats('retries')

            continue
          }
        }

        if (attempt + 1 === maxRetries) {
          this._logAllAttemptsExhausted(requestId, method, attempt + 1, maxRetries)
        }

        /**
         * We decide whether to return the error inside an `AjaxResult` or throw.
         *
         * Delegated to the manager rather than tested here against one list:
         * the decision now also has a category branch for `restApi:v3`, and
         * splitting it across two files is how the payload parsing came to
         * disagree with itself (#423, #460).
         */
        if (this._restrictionManager.isSoftError(lastError)) {
          return this._createAjaxResultWithErrorFromResponse<T>(lastError, requestId, method, params)
        }
        throw lastError
      }
    }

    // Unreachable on normal exhaustion — the final attempt throws `lastError` (its
    // real code) above. Only reached when maxRetries < 1: the loop never runs and
    // there is no lastError to surface. (#143)
    throw new AjaxError({
      code: 'JSSDK_CALL_ALL_ATTEMPTS_EXHAUSTED',
      description: 'All attempts exhausted',
      status: lastError?.status || 500,
      requestInfo: { method, params, requestId },
      originalError: lastError?.originalError || null
    })
  }

  protected _convertToAjaxError(requestId: string, error: unknown, method: string, params: TypeCallParams): AjaxError {
    if (error instanceof AjaxError) {
      return error
    }

    if (error instanceof AxiosError) {
      return this._convertAxiosErrorToAjaxError(requestId, error, method, params)
    }

    return this._convertUnknownErrorToAjaxError(requestId, error, method, params)
  }

  protected _convertAxiosErrorToAjaxError(requestId: string, axiosError: AxiosError, method: string, params: TypeCallParams): AjaxError {
    const errorCode = `${axiosError.code || 'JSSDK_AXIOS_ERROR'}`
    const errorDescription = axiosError.message
    const status = axiosError.response?.status || 0

    // Handling network errors
    if (errorCode === 'ERR_NETWORK') {
      return new AjaxError({
        code: 'NETWORK_ERROR',
        description: 'Network connection failed',
        status: 0,
        requestInfo: { method, params, requestId },
        originalError: axiosError
      })
    }

    // Handling timeout
    if (errorCode === 'ECONNABORTED' || axiosError.message.includes('timeout')) {
      return new AjaxError({
        code: 'REQUEST_TIMEOUT',
        description: 'Request timeout exceeded',
        status: 408,
        requestInfo: { method, params, requestId },
        originalError: axiosError
      })
    }

    // Shared with `AjaxResult.#processErrors()`. Which of the two runs depends on
    // whether the code is soft or hard — not something a caller controls — so
    // they have to agree, and when this was written out in both places they did
    // not: neither read `validation[].field` (#423).
    const parsed = parseErrorPayload(axiosError.response?.data, errorCode, errorDescription)

    return new AjaxError({
      code: parsed?.code ?? errorCode,
      description: parsed?.description ?? errorDescription,
      status,
      validation: parsed?.validation,
      isV3Envelope: parsed?.isV3Envelope,
      requestInfo: { method, params, requestId },
      originalError: axiosError
    })
  }

  protected _convertUnknownErrorToAjaxError(requestId: string, error: unknown, method: string, params: TypeCallParams): AjaxError {
    return new AjaxError({
      code: 'JSSDK_UNKNOWN_ERROR',
      description: error instanceof Error ? error.message : String(error),
      status: 0,
      requestInfo: { method, params, requestId },
      originalError: error
    })
  }

  // region Execute Single Call ////
  /**
   * Performs a single call with
   * - 401 error handling
   * - rate limit check
   * - updating operating statistics
   */
  protected async _executeSingleCall<T = unknown>(requestId: string, method: string, params: TypeCallParams, requestConfig?: AxiosRequestConfig): Promise<AjaxResult<T>> {
    this._checkClientSideWarning(requestId)
    const authData = await this._ensureAuth(requestId)
    const response = await this._makeRequestWithAuthRetry<T>(requestId, method, params, authData, requestConfig)

    return this._createAjaxResultFromResponse<T>(response, requestId, method, params)
  }

  // Get/update authorization
  protected async _ensureAuth(requestId: string): Promise<AuthData> {
    let authData = this._authActions.getAuthData()
    if (authData === false) {
      this._logRefreshingAuthToken(requestId)
      authData = await this._refreshAuth()
    }
    return authData
  }

  /**
   * Refresh the auth token, coalescing concurrent callers onto a single
   * in-flight `refreshAuth()` so a burst of 401s triggers exactly one refresh
   * round-trip. The slot clears once the refresh settles. (#182)
   */
  protected _refreshAuth(): Promise<AuthData> {
    if (this._pendingRefresh) {
      return this._pendingRefresh
    }
    const refresh = this._authActions.refreshAuth()
    this._pendingRefresh = refresh
    // Clear the slot once settled; the extra no-op catch keeps this cleanup
    // chain from surfacing as an unhandled rejection — callers still receive
    // the rejection through the returned promise.
    refresh.finally(() => {
      this._pendingRefresh = null
    }).catch(() => {})
    return refresh
  }

  // Execute the request with 401 error handling
  protected async _makeRequestWithAuthRetry<T>(requestId: string, method: string, params: TypeCallParams, authData: AuthData, requestConfig?: AxiosRequestConfig): Promise<AjaxResponse<T>> {
    try {
      // 4. Apply the rate limit through the manager
      await this._restrictionManager.checkRateLimit(requestId, method)

      return await this._makeAxiosRequest<T>(requestId, method, params, authData, requestConfig)
    } catch (error) {
      if (error instanceof AxiosError) {
        this.getLogger().info(
          `post/catchError`, {
            requestId,
            status: error.status,
            // Redact in case a future portal response embeds credentials in
            // the error body (today it doesn't, but the channel is open) (#39),
            // and cap the length so a large error body can't flood the sink (#236).
            responseData: truncateForLog(JSON.stringify(redactSensitiveParams(error?.response?.data), null, 0))
          }
        ).catch(() => {})
      }

      // Normalize to AjaxError first: axios throws a raw AxiosError here, whose
      // Bitrix `code` (e.g. `expired_token`) is only populated by conversion. The
      // 401 auth-retry check must run against the converted error — otherwise the
      // `instanceof AjaxError` guard in `_isAuthError` is always false and the
      // refresh-and-retry branch below is dead code. (#182)
      const ajaxError = this._convertToAjaxError(requestId, error, method, params)

      // If this is an authorization error (401), then we try to update the token and repeat
      if (this._isAuthError(ajaxError)) {
        this._logAuthErrorDetected(requestId)
        this._logRefreshingAuthToken(requestId)

        const refreshedAuthData = await this._refreshAuth()

        // 4. Apply the rate limit through the manager
        await this._restrictionManager.checkRateLimit(requestId, method)

        return await this._makeAxiosRequest<T>(requestId, method, params, refreshedAuthData, requestConfig)
      }

      // Non-auth error: rethrow the already-converted AjaxError (idempotent in
      // `call()`'s catch) instead of the raw AxiosError. (#182)
      throw ajaxError
    }
  }

  protected async _makeAxiosRequest<T>(requestId: string, method: string, params: TypeCallParams, authData: AuthData, requestConfig?: AxiosRequestConfig): Promise<AjaxResponse<T>> {
    let methodFormatted = this._prepareMethod(requestId, method, this.getBaseUrl())

    // A `restApi:v3` batch sends its commands AS the request body — a bare
    // top-level array, no envelope (see `HttpV3.batch`). Everything else sends an
    // object, and `_prepareParams` is built for that: it spreads `params` into a
    // fresh object, which turns an array into `{ '0': …, '1': … }`, and then adds
    // the OAuth `auth` as one more top-level entry.
    //
    // The portal iterates every top-level entry of a batch body and demands
    // `method` and `query` on each, so that extra entry rejects the whole batch
    // with `INVALIDSELECTEXCEPTION` before a command runs. Three cases follow,
    // decided by where the credential can live:
    //
    // 1. A webhook authenticates through the URL, so the body can be the array
    //    the portal's grammar describes. Measured live: accepted.
    // 2. A non-hook transport on a server sends the token in the standard
    //    header, which the portal accepts and prefers over any body or query
    //    parameter (`rest/lib/oauth/auth.php`, `getAuthKey()`).
    // 3. A non-hook transport **where CORS applies** cannot use that header: the
    //    portal answers the preflight with `Access-Control-Allow-Headers: origin,
    //    content-type, accept` (`CRestUtil::sendHeaders()`), so asking for
    //    `Authorization` fails the preflight and the request never leaves. The
    //    credential goes in the **query string** instead — `?auth=<token>`,
    //    which the portal reads through the same dictionary as a body `auth`
    //    (`CRestUtil::getRequestData()` merges GET and POST with
    //    `array_replace`, so `getAuthKey()` cannot tell the two apart). Measured
    //    accepted on a live portal. A query parameter costs nothing on a
    //    preflight the request is already making for its `Content-Type`.
    //
    //    This is the one place the SDK puts an OAuth token in a URL, so the
    //    reasoning is worth keeping: in a frame app the token is in the page's
    //    JavaScript already — devtools, the body of every non-batch call — so
    //    the query string reveals it to nobody who could not already read it.
    //    What it does add is a server-side record: `?auth=` reaches the portal's
    //    access log and, when an administrator switches the module's diagnostic
    //    logger on, its `REQUEST_URI` field. That was weighed and accepted; the
    //    alternative was leaving every browser app unable to batch on v3 at all,
    //    since this SDK is the only official one that runs in a browser.
    //
    //    Delete this branch the day `authorization` appears in the portal's
    //    `Access-Control-Allow-Headers` — then a browser takes the same header
    //    path as a server and nothing else here needs to change.
    //
    // Case 3 asks about CORS rather than about "is this a server", because those
    // are not the same question. Both are answered by `isBrowserLikeRuntime()`,
    // which counts a worker as browser-like: a worker has no `window.document`,
    // so the older DOM test read it as a server and would have given it the
    // header — in a context where CORS applies exactly as it does on the main
    // thread. See {@link isCorsEnforcedRuntime}.
    //
    // The webhook case is not conditioned on any of this: it adds no header, so
    // there is no preflight to fail. Its body does change shape everywhere,
    // browser included — from `{ '0': … }` to the array — which the portal reads
    // identically, because PHP's `json_decode` cannot tell a list from a map with
    // sequential integer keys.
    //
    // Gated on the version AND the method, not merely on the body being an array.
    // `TypeCallParams` carries an index signature, so an array satisfies it, and
    // `getHttpClient(ApiVersion.v2).call('task.commentitem.getlist', [taskId, …])`
    // — the documented positional shape for the legacy `task.*` family — reaches
    // this function with an array on `restApi:v2`. Everything reasoned about
    // above is about a v3 batch; on v2 the body would change shape AND the
    // credential would move to a header whose acceptance nothing here has
    // established. The comment describes one case, so the code tests for that
    // one case.
    const isV3Batch = ApiVersion.v3 === this._version && 'batch' === method
    const isBareArrayBody = isV3Batch && Array.isArray(params)
    // Load-bearing, not defensive: `AuthHookManager` returns the webhook secret
    // as `access_token` (`hook/auth.ts`), so dropping this term would put a
    // portal-wide secret into an `Authorization` header.
    const isHook = 'hook' === authData.refresh_token
    // An absent or empty token would go out as the literal `Bearer undefined`.
    // `_prepareParams` used to drop it silently — `JSON.stringify` omits an
    // `undefined` value — so require a real one rather than put a nonsense
    // credential on the wire. Trimmed, because a token of blanks is not a token
    // and `Bearer   ` is a malformed header rather than a failed authentication:
    // it earns a portal error that names neither the header nor the token, where
    // the body fallback at least fails the way this transport already fails.
    const hasAccessToken = 'string' === typeof authData.access_token && authData.access_token.trim().length > 0
    // An idempotent call needs the token out of the body too: the portal
    // fingerprints the raw body for `Idempotency-Key`, so a token in it makes a
    // retry after the token rotates look like a different request (422
    // `IDEMPOTENCYKEYREUSED`) instead of a replay. Measured in #570.
    const hasIdempotencyKey = ApiVersion.v3 === this._version
      && 'string' === typeof (requestConfig?.headers as Record<string, unknown> | undefined)?.[IDEMPOTENCY_KEY_HEADER]
    const authOutOfBody = (isBareArrayBody || hasIdempotencyKey) && !isHook && hasAccessToken
    const canSendAuthHeader = authOutOfBody && !isCorsEnforcedRuntime()
    // Same conditions as the header, on the other side of the CORS question: a
    // browser cannot send `Authorization`, so the token rides in the query
    // string. A hook is excluded for the same reason it is excluded above — its
    // credential is already in the URL path, and `_prepareParams` skips it.
    //
    // This branch takes `maxRedirects: 0` as well, for a narrower reason than
    // the header does. A credential in a query string does not follow a redirect
    // the way a header does: `follow-redirects` re-sends `Authorization` to a
    // same-host or subdomain hop, whereas a redirect target is whatever
    // `Location` names and carries the original query only if the server echoes
    // it back. Some do — an nginx or Apache rule built from `$request_uri` on a
    // scheme or trailing-slash hop preserves the query string, and the hop is
    // then a token handed to whatever serves the target.
    //
    // Whether the option bites depends on the adapter, and that is not decided
    // by "is this a browser". `getAdapter()` walks the default
    // `['xhr', 'http', 'fetch']` and takes the first *supported* entry. XHR is
    // supported wherever `XMLHttpRequest` exists — a window, a dedicated worker,
    // a shared worker — and the XHR adapter ignores `maxRedirects` outright. A
    // **service worker** has no `XMLHttpRequest`, so the list falls through
    // `http` (Node only) to `fetch`, and the fetch adapter does read it, as
    // `redirect: 'manual'`. Inert on the common path, load-bearing on that one:
    // reason enough to set it rather than reason to leave it out.
    const useQueryAuth = authOutOfBody && isCorsEnforcedRuntime()
    const sendBareArray = isBareArrayBody && (isHook || canSendAuthHeader || useQueryAuth)

    const paramsFormatted = sendBareArray
      ? params as unknown as TypePrepareParams
      : this._prepareParams(authData, params)

    if (authOutOfBody && !sendBareArray) {
      delete paramsFormatted.auth
    }

    // Merged into whatever the caller's own config already carries — today the
    // `Idempotency-Key` header — rather than replacing it.
    //
    // `maxRedirects: 0` comes with a credential outside the body — the header or
    // the query string — and only with it. A credential in
    // the body was safe across a redirect by accident: a 301/302 turns POST into
    // GET and drops the body, so the old `auth` never travelled. A header does —
    // `follow-redirects` strips `Authorization` when the host changes, but keeps
    // it for the same host and for a **subdomain**, so a portal answering
    // `301 Location: https://cdn.<portal>/…` would hand the token to whatever
    // serves that name.
    //
    // Set here rather than on the axios instance because the instance carries
    // every request the SDK makes, and a redirect somewhere in that traffic may
    // be load-bearing for someone. This branch is different: it is reached only
    // by a v3 batch or an idempotent v3 call on a non-hook transport. For the
    // batch there was no working behaviour to preserve; an idempotent call did
    // follow a redirect before, with its token in a body the redirect dropped,
    // so it failed there anyway — now it fails legibly. A deployment that does
    // redirect gets an error instead of a silent hop with a credential attached —
    // a legible 301 through `validateStatus` on the Node adapter, and on `fetch`,
    // where `redirect: 'manual'` is what `maxRedirects: 0` becomes and the
    // response carries no status of its own, the explicit status-0 check after
    // the `post` below (axios resolves an opaque response rather than rejecting
    // it).
    //
    // Before the spread, not after, so it is a default rather than a lock: a
    // transport that overrides `_prepareRequestConfig` — `HttpV2` already does —
    // can hand back a `maxRedirects` and win. `TypeCallOptions` carries no such
    // field today, so there is no way for an ordinary caller to reopen it, which
    // is the right way round for a credential.
    // The portal reads a `filter` value as a boolean only when the body is JSON.
    // Under `application/x-www-form-urlencoded`, `ACTIVE: false` arrives as the
    // string `"false"`, the condition is dropped, and the call answers with rows
    // it should have excluded — no error on either side. Measured on
    // `user.get` (`filter: { ACTIVE: false }` → 0 rows as JSON, 1 as form data,
    // against the same portal in the same minute).
    //
    // The SDK has always sent JSON, but never asked for it: axios picks
    // `application/json` on its own for a plain-object body. That default is
    // reachable — `ajaxClient` is public and the docs encourage touching it to
    // raise `defaults.timeout` — so one header on the instance silently breaks
    // every boolean filter portal-wide.
    //
    // Per request rather than on the instance, deliberately: a caller who posts
    // `FormData` through `ajaxClient` themselves still gets the multipart
    // boundary axios computes for them. This states what the SDK's own traffic
    // depends on without deciding anything for traffic it does not own.
    //
    // Spread first inside `headers`, so anything a transport puts in
    // `requestConfig.headers` overrides it; none does today. What this does not
    // reach is a request interceptor installed on the public `ajaxClient` — that
    // runs after the config is assembled and can still replace the header (and
    // with it the encoding). Documented rather than defended: the instance is
    // public on purpose.
    const jsonBody = { 'Content-Type': 'application/json' }

    const effectiveConfig: AxiosRequestConfig | undefined = canSendAuthHeader
      ? {
          maxRedirects: 0,
          ...requestConfig,
          headers: {
            ...jsonBody,
            ...requestConfig?.headers,
            Authorization: `Bearer ${authData.access_token}`
          }
        }
      : useQueryAuth
        ? {
            maxRedirects: 0,
            ...requestConfig,
            headers: { ...jsonBody, ...requestConfig?.headers }
          }
        : {
            ...requestConfig,
            headers: { ...jsonBody, ...requestConfig?.headers }
          }

    // `paramsFormatted` carries the OAuth `auth` (access_token) for non-hook flows;
    // log a redacted copy so the secret never enters logger context, while axios
    // still receives the original below. (#39)
    // The `Authorization` header is deliberately NOT logged, and nothing here may
    // start logging it: `redactSensitiveParams` walks `params` and never touches
    // headers, so a token put into logger context would arrive in the clear —
    // the #39 defect, in a channel neither the redactor nor the local
    // `no-credential-in-logger` rule can see. Pinned by a test rather than left
    // to this comment.
    const paramsFormattedForLog = JSON.stringify(redactSensitiveParams(paramsFormatted), null, 0)

    this.getLogger().info(
      `post/send`, {
        requestId,
        method,
        params: truncateForLog(paramsFormattedForLog)
      }
    ).catch(() => {})

    if (useQueryAuth) {
      // `_prepareMethod` may or may not have emitted a query string already
      // (an idempotent non-batch call on a server, if a caller forced the fetch
      // adapter's CORS mode, would too), so ask.
      const separator = methodFormatted.includes('?') ? '&' : '?'
      methodFormatted += `${separator}auth=${encodeURIComponent(authData.access_token)}`
    }

    const response = await this._clientAxios.post<SuccessPayload<T>>(methodFormatted, paramsFormatted, effectiveConfig)

    // A blocked redirect is not an error on every adapter, so say so here.
    // `settle` resolves any response with a falsy status before `validateStatus`
    // runs, and the fetch adapter — what a browser now gets — turns
    // `redirect: 'manual'` into an opaque response: status 0, empty body, no
    // headers. The XHR adapter rejected that shape itself (`ECONNABORTED`);
    // fetch does not, so without this the caller gets a successful, silently
    // empty answer to a request the SDK deliberately refused to follow.
    //
    // The **effective** value, not only the per-request one: `maxRedirects` is
    // an allowed `httpOptions` key, and an instance-level `0` makes every
    // request `redirect: 'manual'` on the fetch adapter — where the per-request
    // config is absent, which left a blocked redirect resolving as an empty
    // success on an ordinary call.
    //
    // That scope is wider than the SDK's own branch, and deliberately so: an
    // opaque response carries nothing to tell a blocked redirect from a dropped
    // connection, and a request that asked not to follow redirects is better
    // answered with an error than with a silently empty success. The cost is
    // that a caller who sets `maxRedirects: 0` themselves puts every status-0
    // answer in this bucket, retries included — stated in the error text and on
    // the Http page rather than left to be discovered. A caller who does not set
    // it is unaffected: the only branches that set it are a `restApi:v3` batch
    // and an idempotent `restApi:v3` call, both on a non-hook transport.
    const effectiveMaxRedirects = effectiveConfig?.maxRedirects
      ?? this._clientAxios.defaults.maxRedirects

    if (0 === effectiveMaxRedirects && 0 === response.status) {
      throw new AjaxError({
        code: 'JSSDK_HTTP_REDIRECT_BLOCKED',
        description: 'This request does not follow redirects (`maxRedirects: 0`) and the answer carried no status of its own — '
          + 'which is what a refused redirect looks like on the fetch adapter, and what a dropped connection can look like too. '
          + 'The SDK sets `maxRedirects: 0` on a `restApi:v3` batch and on a `restApi:v3` call with `idempotencyKey`, both on a '
          + 'non-hook transport, because they carry an access token a redirect would take along. Point the SDK at the final URL instead.',
        status: 0,
        requestInfo: { method, params, requestId }
      })
    }

    // Redact the log-bound copy only; callers still receive the untouched
    // `response.data` below. First-party success bodies don't embed credentials
    // today, but an OAuth-relay method (or a future method returning a canonical
    // key) would otherwise leak `access_token` / `refresh_token` / etc. into any
    // wired logger sink — mirror the `post/send` redaction on the success path. (#69)
    // A v3 method outside the `result` envelope (e.g. `rest.documentation.openapi`)
    // makes this `undefined` rather than a string; `truncateForLog` coerces it. (#338)
    // `?.` on the body itself, not only on `.result`: a success is not always an
    // object. An HTTP 200 whose body is `null` made this line throw, and the
    // request path then re-wrapped that `TypeError` as `JSSDK_UNKNOWN_ERROR` —
    // a fine response reaching the caller as an unrecognisable failure, the same
    // shape as #338 and #456 one field over.
    const resultFormattedForLog = JSON.stringify(redactSensitiveParams(response.data?.result), null, 0)
    this.getLogger().info(
      `post/response`, {
        requestId,
        // responseFull: JSON.stringify(response.data, null, 2),
        result: truncateForLog(resultFormattedForLog),
        time: JSON.stringify(response.data?.time, null, 0)
      }
    ).catch(() => {})

    const idempotency = readIdempotencyHeaders(response.headers)

    return {
      status: response.status,
      payload: response.data,
      ...(idempotency ? { idempotency } : {})
    }
  }

  /**
   * Builds the per-request axios config for one call, or `undefined` when the
   * call needs none.
   *
   * A `protected` hook rather than a branch inside `_makeAxiosRequest` because
   * the two transports genuinely disagree about one option: `HttpV2` overrides
   * it to drop `idempotencyKey`, since the v2 endpoint ignores the header and a
   * key that is silently dropped leaves a caller believing a retry is
   * deduplicated when it is not. It is the mechanism for that disagreement, not
   * a speculative extension point — a subclass overriding it owes the same
   * contract: return per-request axios config, or `undefined` for none.
   *
   * @throws {SdkError} `JSSDK_HTTP_INVALID_IDEMPOTENCY_KEY` when the key is not
   *   1-255 printable ASCII characters.
   * @throws {SdkError} `JSSDK_HTTP_IDEMPOTENCY_KEY_BROWSER` when a v3 call with a
   *   key runs where CORS applies (#573).
   */
  protected _prepareRequestConfig(_requestId: string, _method: string, options?: TypeCallOptions): AxiosRequestConfig | undefined {
    const idempotencyKey = options?.idempotencyKey

    if (undefined === idempotencyKey) {
      return undefined
    }

    if (!IDEMPOTENCY_KEY_RE.test(idempotencyKey)) {
      // Static text only — never the key itself. `SdkError` does not run its
      // description through `redactSensitiveParams`, and a caller is free to
      // build a key out of business identifiers.
      throw new SdkError({
        code: 'JSSDK_HTTP_INVALID_IDEMPOTENCY_KEY',
        description: '`idempotencyKey` must be 1-255 printable ASCII characters with no whitespace or control characters. '
          + 'A `crypto.randomUUID()` value satisfies this. See https://apidocs.bitrix24.ru/api-reference/rest-v3.html',
        status: 500
      })
    }

    // A browser never sends it: the portal's CORS preflight allows only
    // `origin, content-type, accept`, and no query or body form is honoured
    // (measured, #573). Fail here, before anything is sent, instead of as a
    // network error the retry loop would repeat.
    if (ApiVersion.v3 === this._version && isCorsEnforcedRuntime()) {
      throw new SdkError({
        code: 'JSSDK_HTTP_IDEMPOTENCY_KEY_BROWSER',
        description: '`idempotencyKey` cannot be used from a browser: the portal\'s CORS preflight does not allow the '
          + '`Idempotency-Key` header, so a cross-origin request carrying it is refused. Make idempotent writes from a server. See https://github.com/bitrix24/b24jssdk/issues/573',
        status: 500
      })
    }

    return { headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey } }
  }

  protected _isAuthError(error: unknown): boolean {
    if (!(error instanceof AjaxError)) {
      return false
    }

    return (
      error.status === 401
      && ['expired_token', 'invalid_token'].includes(error.code)
    )
  }

  protected async _createAjaxResultFromResponse<T>(response: AjaxResponse<T>, requestId: string, method: string, params: TypeCallParams): Promise<AjaxResult<T>> {
    const result = new AjaxResult<T>({
      answer: response.payload,
      query: { method, params, requestId },
      status: response.status,
      idempotency: response.idempotency
    })

    // 5. Update operating statistics — only when the portal sent a `time` block.
    // It does not always: `rest.documentation.openapi` answers with the OpenAPI
    // document at the top level, with neither a `result` envelope nor `time`.
    // The `time!` assertion that used to stand here claimed otherwise, and
    // `OperatingLimiter.updateStats()` destructured the absent block — so a
    // perfectly good HTTP 200 came back to the caller as an exception.
    if (result.isSuccess) {
      const time = result.getData()?.time
      if (time) {
        await this._restrictionManager.updateStats(requestId, method, time)
      }
    }

    return result
  }

  /**
   * Turns an error the transport already built into the soft `AjaxResult` a
   * caller receives, for the codes in `RestrictionManager.exceptionCodeForSoft`.
   *
   * It used to rebuild the error from a synthetic answer holding only `code` and
   * `message`, so the portal's real body was discarded here — which is why
   * `validation` was unreachable even though `_convertAxiosErrorToAjaxError` had
   * just parsed it (#423).
   *
   * The error is now **carried** rather than re-derived: the synthetic `answer`
   * is kept so `_data` still describes the failure for anything reading it, but
   * it is no longer what produces the error — which also means the two can no
   * longer disagree. `validation` is deliberately not copied into that synthetic
   * answer: nothing parses it back out, so it would be dead weight that a future
   * refactor could mistake for the source of truth.
   *
   * The carried error keeps its `originalError` — the raw `AxiosError`, whose
   * `config.url` holds the webhook secret. It is non-enumerable (see
   * `SdkError`), so spreads and `JSON.stringify` still cannot reach it, but it
   * is now readable via `result.getErrors()` on this path as well as on the
   * throwing one. That is deliberate: the two paths differ only in how the error
   * is delivered, and a caller debugging one should not find less on the other.
   */
  protected _createAjaxResultWithErrorFromResponse<T>(ajaxError: AjaxError, requestId: string, method: string, params: TypeCallParams): AjaxResult<T> {
    return new AjaxResult<T>({
      answer: {
        error: {
          code: ajaxError.code,
          message: ajaxError.message
        }
      },
      query: { method, params, requestId },
      status: ajaxError.status,
      // The error itself, not a reconstruction: it was parsed from the portal's
      // body a moment ago, and re-deriving it here would fold the validation
      // messages onto a description that already holds them (#423).
      error: ajaxError
    })
  }
  // endregion ////
  // endregion ////

  // region Prepare ////
  /**
   * Builds the request URL: the method path plus the SDK telemetry query params
   * (`bx24_request_id` / `bx24_sdk_ver` / `bx24_sdk_type` — request tracing and
   * SDK identification, not auth material).
   *
   * Carve-out for the legacy positional `task.*` methods (`task.commentitem.*`,
   * `task.checklistitem.*`, `task.elapseditem.*`, …): these read the request
   * **query string positionally**, so appending the telemetry params shifts
   * `Param #0` and the server rejects the call —
   * `WRONG_ARGUMENTS: Param #0 (taskId) ... expected integer, but given
   * something else`. Verified live against a portal: the same
   * `task.commentitem.getlist` / `task.checklistitem.getlist` call succeeds
   * without the telemetry params and fails with them; modern `tasks.task.*`
   * (named params) is unaffected. So telemetry is omitted only for methods whose
   * name STARTS WITH `task.`.
   *
   * Shared by v2 and v3 (rather than per-transport): once the v3 method
   * allowlist was dropped (#259) a positional `task.*` method can be routed via
   * `actions.v3.*` too, so v3 needs the same suppression — keeping the rule in
   * one place stops the two transports drifting apart again (#207).
   *
   * The match is anchored (`^task\.`): only legacy positional `task.*` methods
   * are suppressed. Modern named-param methods `tasks.task.*` / `bizproc.task.*`
   * do NOT start with `task.`, so they KEEP telemetry and stay traceable — the
   * boundary was pinned live in #271/#272 (`tasks.task.list` works WITH
   * telemetry; legacy `task.*` breaks WITH it). Bitrix24 method names are
   * lowercase by convention, so the case-sensitive match is sufficient.
   *
   * @see https://apidocs.bitrix24.com/settings/how-to-call-rest-api/data-encoding.html#order-of-parameters
   */
  protected _prepareMethod(requestId: string, method: string, baseUrl: string): string {
    const methodUrl = `/${encodeURIComponent(method)}`

    if (/^task\./.test(method)) {
      return `${baseUrl}${methodUrl}`
    }

    const queryParams = new URLSearchParams({
      [this._requestIdGenerator.getQueryStringParameterName()]: requestId,
      [this._requestIdGenerator.getQueryStringSdkParameterName()]: '__SDK_VERSION__',
      [this._requestIdGenerator.getQueryStringSdkTypeParameterName()]: '__SDK_USER_AGENT__'
    })
    return `${baseUrl}${methodUrl}?${queryParams.toString()}`
  }

  /**
   * Processes function parameters and adds authorization
   */
  protected _prepareParams(authData: AuthData, params: TypeCallParams): TypePrepareParams {
    const result: TypePrepareParams = { ...params }

    /** @memo we skip auth for hook */
    if (authData.refresh_token !== 'hook') {
      result.auth = authData.access_token
    }

    if (result?.data && 'start' in result.data) {
      const { start, ...dataWithoutStart } = result.data
      result.data = dataWithoutStart
    }

    return result
  }

  /**
   * @inheritDoc
   */
  public setClientSideWarning(
    value: boolean,
    message: string
  ): void {
    this._isClientSideWarning = value
    this._clientSideWarningMessage = message
  }
  // endregion ////

  // region Tools ////
  /**
   * Tests whether the code is running outside a browser-like runtime — that is,
   * on a server. The inverse of {@link isBrowserLikeRuntime}, and a worker is
   * **not** server-side: it has no DOM, but the browser's rules apply to it.
   *
   * @return {boolean}
   * @protected
   */
  protected isServerSide(): boolean {
    return !isBrowserLikeRuntime()
  }

  /**
   * Get the BX24 account address with the path based on the API version
   */
  public getBaseUrl(): string {
    return this._authActions.getTargetOriginWithPath().get(this._version)!
  }
  // endregion ////

  // region Log ////
  /**
   * Redaction contract: runs caller params through {@link redactSensitiveParams}
   * (see `redact.ts`) so credential-bearing keys are masked before they reach any
   * logger context. (#39, #73)
   * @see redactSensitiveParams
   */
  protected _sanitizeParams(params: TypeCallParams): Record<string, unknown> {
    return redactSensitiveParams(params)
  }

  /**
   * Redaction contract: params are redacted via {@link _sanitizeParams} →
   * {@link redactSensitiveParams} before logging. (#73)
   * @see redactSensitiveParams
   */
  protected _logRequest(requestId: string, method: string, params: TypeCallParams): void {
    this.getLogger().debug(`http request starting`, {
      requestId,
      method,
      params: this._sanitizeParams(params),
      api: this.apiVersion,
      timestamp: Date.now()
    }).catch(() => {})
  }

  protected _logAttempt(requestId: string, method: string, attempt: number, maxRetries: number): void {
    this.getLogger().info(`http request attempt`, {
      requestId,
      method,
      api: this.apiVersion,
      attempt: {
        current: attempt,
        max: maxRetries
      }
    }).catch(() => {})
  }

  protected _logRefreshingAuthToken(requestId: string): void {
    this.getLogger().info(`http refreshing auth token`, {
      requestId,
      api: this.apiVersion
    }).catch(() => {})
  }

  protected _logAuthErrorDetected(requestId: string): void {
    this.getLogger().info(`http auth error detected`, {
      requestId,
      api: this.apiVersion
    }).catch(() => {})
  }

  protected _logSuccessfulRequest(requestId: string, method: string, duration: number): void {
    this.getLogger().debug(`http request successful`, {
      requestId,
      method,
      api: this.apiVersion,
      duration: {
        ms: duration,
        sec: Number.parseFloat((duration / 1000).toFixed(2))
      }
    }).catch(() => {})
  }

  protected _logFailedRequest(
    requestId: string,
    method: string,
    attempt: number,
    maxRetries: number,
    error: AjaxError
  ): void {
    this.getLogger().debug(`http request failed`, {
      requestId,
      method,
      api: this.apiVersion,
      attempt: {
        current: attempt,
        max: maxRetries
      },
      error: {
        code: error.code,
        message: error.message,
        status: error.status
      }
    }).catch(() => {})
  }

  protected _logAttemptRetryWaiteDelay(
    requestId: string,
    method: string,
    wait: number,
    attempt: number,
    maxRetries: number
  ): void {
    this.getLogger().debug(
      `http wait ${(wait / 1000).toFixed(2)} sec.`,
      {
        requestId,
        method,
        api: this.apiVersion,
        wait: wait,
        attempt: {
          current: attempt,
          max: maxRetries
        }
      }
    ).catch(() => {})
  }

  protected _logAllAttemptsExhausted(requestId: string, method: string, attempt: number, maxRetries: number): void {
    this.getLogger().warning(`http all retry attempts exhausted`, {
      requestId,
      method,
      api: this.apiVersion,
      attempt: {
        current: attempt,
        max: maxRetries
      }
    }).catch(() => {})
  }

  protected _logBatchStart(
    requestId: string,
    calls: BatchCommandsArrayUniversal | BatchCommandsObjectUniversal | BatchNamedCommandsUniversal,
    options: ICallBatchOptions
  ): void {
    const callCount = Array.isArray(calls)
      ? calls.length
      : Object.keys(calls).length

    this.getLogger().debug(`http batch request starting `, {
      requestId,
      callCount,
      api: this.apiVersion,
      isHaltOnError: options.isHaltOnError,
      timestamp: Date.now()
    }).catch(() => {})
  }

  protected _logBatchCompletion(requestId: string, total: number, errors: number): void {
    this.getLogger().debug(`http batch request completed`, {
      requestId,
      api: this.apiVersion,
      totalCalls: total,
      successful: total - errors,
      failed: errors,
      successRate: total > 0 ? ((total - errors) / (total) * 100).toFixed(1) + '%' : '??'
    }).catch(() => {})
  }

  // Check client-side warnings
  protected _checkClientSideWarning(requestId: string): void {
    if (
      this._isClientSideWarning
      && !this.isServerSide()
      && Type.isStringFilled(this._clientSideWarningMessage)
    ) {
      LoggerFactory.forcedLog(
        this.getLogger(),
        'warning',
        this._clientSideWarningMessage,
        {
          requestId,
          code: 'JSSDK_CLIENT_SIDE_WARNING'
        }
      )
    }
  }
  // endregion ////
}
